import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, CheckCircle2, X } from "lucide-react";
import { jsPDF } from "jspdf";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import {
  getBaselines,
  getBaseline,
  deprecateBaseline,
  pauseFirmwareRollout,
  resumeFirmwareRollout,
  cancelDeprecation,
  postRolloutCountrywiseDeviceMapping,
  getCountryWiseDeviceList,
  getRolloutCohort,
} from "../../lib/api";
import { Spinner } from "../../components/ui/spinner";
import { formatTargetCountries } from "../../lib/utils";

function formatSize(bytes) {
  if (!bytes) return "-";
  return (bytes / 1048576).toFixed(1) + " MB";
}

function formatPublished(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "2-digit", day: "2-digit" }).replace(/\//g, "-")
    + " " + d.toTimeString().slice(0, 5);
}

const STATE_STYLE = {
  ROLLOUT:    { dot: "bg-indigo-500",  pill: "border border-indigo-300 text-indigo-700 bg-indigo-50",   label: "ROLLOUT" },
  ROLLBACK:   { dot: "bg-red-500",     pill: "border border-red-300 text-red-700 bg-red-50",           label: "ROLLBACK" },
  PAUSED:     { dot: "bg-amber-500",   pill: "border border-amber-300 text-amber-800 bg-amber-50",       label: "PAUSED" },
  SIGNED:     { dot: "bg-blue-500",    pill: "border border-blue-300 text-blue-700 bg-blue-50",         label: "SIGNED" },
  APPROVED:   { dot: "bg-teal-500",    pill: "border border-teal-300 text-teal-700 bg-teal-50",         label: "APPROVED" },
  PUBLISHED:  { dot: "bg-emerald-500", pill: "border border-emerald-300 text-emerald-700 bg-emerald-50",   label: "PUBLISHED" },
  DEPRECATED: { dot: null,             pill: "border border-slate-300 text-slate-400 bg-white",          label: "DEPRECATED" },
  DRAFT:      { dot: "bg-yellow-400",  pill: "border border-yellow-300 text-yellow-700 bg-yellow-50",        label: "DRAFT" },
  PENDING:    { dot: "bg-amber-400",   pill: "border border-amber-300 text-amber-700 bg-amber-50",       label: "PENDING" },
};

const FILTERS = ["All", "Draft", "Approved", "Published", "Rollout", "Paused", "Rollback", "Deprecated"];

// Registry columns (FMP-BL-001). `sort` is the key baseline-firmware-lambda sorts
// by before it paginates (SORT_FIELDS there); a column without one is not sortable.
const COLUMNS = [
  { label: "VERSION",      sort: "version" },
  { label: "STATE",        sort: "state" },
  { label: "TYPE",         sort: "type" },
  { label: "SHA-256" },
  { label: "SIZE",         sort: "size" },
  { label: "BUILD DATE",   sort: "build_date" },
  { label: "BUILD TIME" },
  { label: "SEGMENT",      sort: "segment" },
  { label: "COUNTRIES" },
  { label: "LAST UPDATED", sort: "published_at" },
  { label: "ACTIONS" },
  { label: "" },
];

function shortHash(sha256) {
  return sha256 ? `${sha256.slice(0, 12)}…` : null;
}

// Release managers are shown as Fleet Admin on the deprecation audit line.
function roleDisplayLabel(role) {
  if (role === "FleetAdmin" || role === "ReleaseManager") return "Fleet Admin";
  return role || "";
}

// Tri-state flag rendering: null/undefined is unknown, not "No".
function yesNoDash(value) {
  if (value == null) return "—";
  return value ? "Yes" : "No";
}

// The rollout tier set at publish, with the share of the fleet it caps a rollout
// to. Records published before tiering shipped carry no tier.
function tierLabel(tier) {
  const labels = {
    canary: "Canary (up to 5%)",
    extended: "Extended (up to 25%)",
    full: "Full Fleet (up to 100%)",
  };
  return labels[String(tier || "").toLowerCase()] || "—";
}

// Empty-state copy for the baselines table: a load failure wins, then the
// narrowing caused by the search box, then by the state filter.
function emptyBaselinesMessage(loadError, search, activeFilter) {
  if (loadError) return <span className="text-red-500">Failed to load baselines: {loadError}</span>;
  if (search) {
    // Hoisted out of the template literal so the two are not nested (S4624).
    const scope = activeFilter !== "All" ? ` in ${activeFilter}` : "";
    return `No baselines match "${search}"${scope}.`;
  }
  if (activeFilter !== "All") return `No baselines in ${activeFilter}.`;
  return "No baselines yet.";
}

// HH:MM:SS for either spelling of the build-time field.
function formatBuildTime(baseline) {
  const raw = baseline.build_time || baseline.buildTime;
  if (!raw) return "—";
  return new Date(raw).toISOString().slice(11, 19);
}

function rolloutButtonLabel(isLoading, isInitiated) {
  if (isLoading) return "Loading...";
  if (isInitiated) return "Rollout Initiated";
  return "Rollout";
}

function actionBtnClass(danger, navy) {
  if (danger) return "border-red-300 text-red-600 hover:bg-red-50";
  if (navy) return "border-[#08148C] bg-[#08148C] text-white hover:bg-[#0a1fa8]";
  return "border-slate-300 text-slate-600 hover:bg-slate-50";
}

/* ── Pause / Resume Rollout Modal ─────────────────────────────────────── */
// Resume is the only way a rollout the canary breaker paused goes back to
// delivering, so the service requires a reason and audits it. Pause takes an
// optional one.
function RolloutHoldModal({ baseline, action, onConfirm, onCancel, loading, error }) {
  const [reason, setReason] = useState("");
  const isResume = action === "resume";
  const autoPaused = baseline.pause_reason === "canary_failure_threshold";
  const canSubmit = !loading && (!isResume || reason.trim().length >= 5);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Decorative backdrop; keyboard users cancel via the Cancel button. */}
      <div className="absolute inset-0 bg-black/40" aria-hidden="true" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 space-y-4">
        <h2 className="text-lg font-bold text-slate-900">
          {isResume ? "Resume" : "Pause"} rollout of {baseline.version}?
        </h2>
        <p className="text-sm text-slate-600">
          {isResume
            ? "Devices in the rollout cohort will be able to receive this firmware again, and the rollout can be widened. The failure count restarts from now."
            : "No further devices will receive this firmware, and the rollout cannot be widened, until it is resumed."}
        </p>
        {isResume && autoPaused && baseline.pause_reported_count != null && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800">
            Paused automatically: {baseline.pause_failed_count}/{baseline.pause_reported_count} canary devices failed
            {baseline.pause_failure_pct != null && ` (${baseline.pause_failure_pct}%)`}.
          </div>
        )}
        <div>
          <label htmlFor="rollout-hold-reason" className="block text-sm font-semibold text-slate-700 mb-1.5">
            Reason {isResume ? <span className="text-red-500">*</span> : <span className="text-slate-400 font-normal">(optional)</span>}
          </label>
          <textarea
            id="rollout-hold-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
          />
        </div>
        <button
          disabled={!canSubmit}
          onClick={() => onConfirm(reason.trim())}
          className="w-full bg-[#08148C] hover:bg-[#060f6e] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold py-3 rounded-xl transition-colors"
        >
          {loading ? (isResume ? "Resuming..." : "Pausing...") : (isResume ? "Resume rollout" : "Pause rollout")}
        </button>
        {error && <p className="text-xs text-red-500 text-center">{error}</p>}
        <button
          disabled={loading}
          onClick={onCancel}
          className="w-full border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold py-3 rounded-xl transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ── Deprecate Confirmation Modal ─────────────────────────────────────── */
function DeprecateModal({ baseline, onConfirm, onCancel, loading, error }) {
  const { user, role } = useAuth();
  const [reason, setReason] = useState("");

  const roleLabel = roleDisplayLabel(role);
  // FMP-DP-004 "the exact actor": the email is what the service records as
  // actor_name, so it is shown alongside the display name.
  const displayName = user?.name || "Unknown";
  const actorEmail = user?.email && user.email !== displayName ? ` <${user.email}>` : "";
  // Taken once when the dialog opens, and in real UTC - toTimeString() gave local
  // time under a "UTC" label (19:26 shown at 13:56 UTC).
  const [openedAt] = useState(() => new Date());
  const timestamp = openedAt.toISOString().slice(0, 16).replace("T", " ") + " UTC";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Decorative backdrop; keyboard users cancel via the Cancel button. */}
      <div className="absolute inset-0 bg-black/40" aria-hidden="true" onClick={onCancel} />

      {/* Modal card */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 space-y-4">

        {/* Title row */}
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center text-xl shrink-0">
            &#x26A0;&#xFE0F;
          </div>
          <h2 className="text-lg font-bold text-slate-900">Deprecate {baseline.version}?</h2>
        </div>

        {/* Warning text */}
        <p className="text-sm text-slate-600">
          This action is irreversible. The baseline state will change to{" "}
          <strong>DEPRECATED</strong> and no new devices will be able to receive this
          firmware. This event is immutably audit-logged.
        </p>

        {/* Device impact box */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          {/* FMP-DP-001: the count comes from the service; when it could not be
              counted the dialog says so rather than showing a misleading 0 (DISC-033). */}
          <p className="text-2xl font-bold text-amber-700">
            {baseline.devicesUpToDate == null
              ? "Device count unavailable"
              : `${baseline.devicesUpToDate} ${baseline.devicesUpToDate === 1 ? "device" : "devices"}`}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            currently running {baseline.version} &middot; Deprecating stops new deliveries of this
            build; these devices keep it until they install a newer baseline.
          </p>
        </div>

        {/* Reason textarea */}
        <div>
          <label htmlFor="deprecate-reason" className="block text-sm font-semibold text-slate-700 mb-1.5">
            Deprecation Reason <span className="text-red-500">*</span>
          </label>
          <textarea
            id="deprecate-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Superseded by v1.5.0 (mandatory). Known BLE timeout regression resolved in v1.5.0."
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
          />
        </div>

        {/* Audit log preview */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-xs text-slate-400 leading-5">
          <span className="font-semibold text-slate-500 block mb-1">Audit log entry will record:</span>
          Actor: {displayName}{actorEmail} ({roleLabel}) &middot; Action: DEPRECATE &middot; Version: {baseline.version} &middot; Timestamp: {timestamp}
        </div>

        {/* Confirm button */}
        <button
          disabled={reason.trim().length < 5 || loading}
          onClick={() => onConfirm(reason)}
          className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold py-3 rounded-xl transition-colors"
        >
          {loading ? "Deprecating..." : `Deprecate ${baseline.version} — I Understand`}
        </button>

        {error && <p className="text-xs text-red-500 text-center">{error}</p>}

        {/* Cancel button */}
        <button
          disabled={loading}
          onClick={onCancel}
          className="w-full border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold py-3 rounded-xl transition-colors"
        >
          Cancel &mdash; Keep {baseline.version} Active
        </button>
      </div>
    </div>
  );
}

/* ── Baseline Summary Overlay ─────────────────────────────────────────── */
// mode "history" is the one action a DEPRECATED row exposes (WP-BL-03, TC-0011):
// the same record, headed "History", with its lifecycle - uploaded, approved,
// published, deprecated and why. mode "summary" is unchanged.
function BaselineSummaryOverlay({ id, onClose, mode = "summary" }) {
  const isHistory = mode === "history";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cohortData, setCohortData] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    (isHistory ? getBaseline(id, { view: "history" }) : getBaseline(id))
      .then((res) => setData(res))
      .catch((err) => setError(err.message || "Failed to load baseline."))
      .finally(() => setLoading(false));
  }, [id, isHistory]);

  // The cohort is a second request because it lives in a different table. Its
  // failure is deliberately NOT surfaced as a page error: the summary is still
  // perfectly readable without the rollout panel, and blanking the whole page
  // because one panel could not load would be the worse outcome.
  const loadCohort = useCallback(() => {
    if (isHistory) return;
    getRolloutCohort(id)
      .then(setCohortData)
      .catch(() => setCohortData(null));
  }, [id, isHistory]);

  useEffect(() => { loadCohort(); }, [loadCohort]);

  const parseTs = (v) => {
    if (!v) return null;
    let ms;
    if (typeof v === "number") {
      // Epoch seconds and epoch milliseconds both appear in the API payloads.
      ms = v < 1e10 ? v * 1000 : v;
    } else {
      ms = Date.parse(v);
    }
    return isNaN(ms) ? String(v) : new Date(ms).toLocaleString("en-GB", { dateStyle: "long", timeStyle: "short" });
  };

  const fmtSize = (v) => {
    const b = parseInt(v, 10);
    if (isNaN(b)) return v || "—";
    return b >= 1048576 ? (b / 1048576).toFixed(2) + " MB" : (b / 1024).toFixed(1) + " KB";
  };

  const buildRows = (d) => {
    const raw = d?.data ?? d?.firmware ?? d ?? {};
    return [
      { label: "Firmware ID",        value: raw.id || "—" },
      { label: "Version",            value: raw.version_string || raw.version || "—" },
      { label: "File Name",          value: raw.file_name || "—" },
      { label: "File Size",          value: raw.file_size ? fmtSize(raw.file_size) : "—" },
      { label: "Status",             value: raw.status ? raw.status.charAt(0).toUpperCase() + raw.status.slice(1) : "—" },
      { label: "Is Mandatory",       value: yesNoDash(raw.is_mandatory) },
      { label: "Min Device FW",      value: raw.min_device_fw_required || "—" },
      { label: "Target Countries",   value: formatTargetCountries(raw.target_countries ?? raw.country) },
      { label: "Segment",            value: raw.target_segment || "—" },
      { label: "Rollout Tier",       value: tierLabel(raw.rollout_tier) },
      { label: "Encrypted ID",       value: raw.encrypted_id || "—", mono: true },
      { label: "SHA-256",            value: raw.sha256 || "—", mono: true },
      { label: "Uploaded At",        value: parseTs(raw.uploaded_at ?? raw.uploadedAt) || "—" },
      { label: "Published At",       value: parseTs(raw.published_at ?? raw.publishedAt ?? raw.uploaded_at ?? raw.uploadedAt) || "—" },
    ];
  };

  // Lifecycle events, oldest first, from fields the record already carries. The
  // deprecation comes from the service's `deprecation` block (Deprecation-Table).
  const buildHistory = (d) => {
    const raw = d?.data ?? d?.firmware ?? d ?? {};
    const dep = raw.deprecation || {};
    return [
      { event: "Uploaded",   at: raw.uploaded_at ?? raw.uploadedAt, by: raw.uploader_name || raw.uploader_email },
      { event: "Approved",   at: raw.approved_at, by: raw.approved_by },
      { event: "Published",  at: raw.published_at ?? raw.publishedAt, by: raw.publisher_name || raw.publisher_email },
      { event: "Deprecated", at: dep.deprecated_at ?? raw.deprecated_at, by: null,
        note: dep.deprecation_reason ?? raw.deprecation_reason },
    ].filter((e) => e.at || e.note);
  };

  const handleDownload = () => {
    const raw = data?.data ?? data?.firmware ?? data ?? {};
    const version = raw.version_string || raw.version || "baseline";
    const publishedAt = parseTs(raw.published_at ?? raw.publishedAt ?? raw.uploaded_at ?? raw.uploadedAt) || new Date().toLocaleString();
    const rows = buildRows(data).map(({ label, value }) => [label, value]);

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const margin = 48;
    let y = 52;

    doc.setFillColor(8, 20, 140);
    doc.rect(margin, y, W - margin * 2, 42, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text("Baseline Published — Publish Summary", margin + 16, y + 26);
    y += 58;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(`Tetra Micro Infusion System  ·  Fleet Management Portal  ·  Generated ${publishedAt}`, margin, y);
    y += 24;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text("PUBLISH DETAILS", margin, y);
    y += 10;

    const col1 = margin;
    const col2 = margin + 180;
    const rowH = 28;

    doc.setFillColor(248, 250, 252);
    doc.rect(col1, y, W - margin * 2, rowH, "F");
    doc.setDrawColor(226, 232, 240);
    doc.rect(col1, y, W - margin * 2, rowH, "S");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(148, 163, 184);
    doc.text("FIELD", col1 + 10, y + 18);
    doc.text("VALUE", col2 + 10, y + 18);
    y += rowH;

    const lineH = 14;
    const rowPadV = 10;
    rows.forEach(([label, value], i) => {
      const maxW = W - margin * 2 - 190;
      const lines = doc.splitTextToSize(String(value), maxW);
      const dynamicRowH = Math.max(28, lines.length * lineH + rowPadV * 2);
      const bg = i % 2 === 0 ? [255, 255, 255] : [248, 250, 252];
      doc.setFillColor(...bg);
      doc.rect(col1, y, W - margin * 2, dynamicRowH, "F");
      doc.setDrawColor(241, 245, 249);
      doc.rect(col1, y, W - margin * 2, dynamicRowH, "S");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139);
      doc.text(label, col1 + 10, y + rowPadV + lineH * 0.75);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(15, 23, 42);
      doc.text(lines, col2 + 10, y + rowPadV + lineH * 0.75);
      y += dynamicRowH;
    });

    doc.save(`publish-summary-${version}.pdf`);
  };

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-auto flex flex-col items-center justify-start px-6 py-12">
      <div className="w-full max-w-2xl space-y-5">
        {loading && (
          <div className="flex items-center justify-center gap-3 py-20">
            <Spinner size="lg" />
            <span className="text-slate-400 text-sm">Loading summary...</span>
          </div>
        )}
        {error && (
          <div className="text-center py-20">
            <p className="text-red-500 text-sm font-medium">{error}</p>
            <button onClick={onClose} className="mt-4 text-sm text-slate-500 underline">Close</button>
          </div>
        )}
        {!loading && !error && data && (() => {
          const raw = data?.data ?? data?.firmware ?? data ?? {};
          const publishedAt = parseTs(raw.published_at ?? raw.publishedAt ?? raw.uploaded_at ?? raw.uploadedAt);
          const rows = buildRows(data);
          const history = isHistory ? buildHistory(data) : [];
          // The rollout panel and its controls only mean anything once a cohort
          // exists: 'published' is approved-but-not-reaching-anything, and a
          // deprecated or rolled-back build is not one to widen.
          const isRollingOut = String(raw.status || "").trim().toLowerCase() === "rollout";
          return (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">{isHistory ? "History" : "Publish Summary"}</h2>
                    {publishedAt && <p className="text-xs text-slate-400">Published {publishedAt}</p>}
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="flex items-center gap-1.5 text-sm font-semibold text-white bg-[#08148C] hover:bg-[#060f6e] px-5 py-2 rounded-lg transition-colors"
                >
                  &#8592; Back to Baseline view
                </button>
              </div>

              {isHistory && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="px-6 py-3 border-b border-slate-100 bg-slate-50">
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Lifecycle History</span>
                  </div>
                  {history.length === 0 ? (
                    <p className="px-6 py-3 text-sm text-slate-400">No lifecycle events recorded.</p>
                  ) : (
                    <ol className="divide-y divide-slate-100">
                      {history.map((h) => (
                        <li key={h.event} className="flex items-start justify-between px-6 py-3 gap-6">
                          <span className="text-sm font-medium text-slate-400 shrink-0 w-44">{h.event}</span>
                          <span className="text-sm text-right text-slate-800 font-medium">
                            {parseTs(h.at) || "—"}
                            {h.by && <span className="block text-xs text-slate-500 font-normal">by {h.by}</span>}
                            {h.note && <span className="block text-xs text-slate-500 font-normal">Reason: {h.note}</span>}
                          </span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              )}

              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-6 py-3 border-b border-slate-100 bg-slate-50">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Publish Details</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {rows.map(({ label, value, mono }) => (
                    <div key={label} className="flex items-start justify-between px-6 py-3 gap-6">
                      <span className="text-sm font-medium text-slate-400 shrink-0 w-44">{label}</span>
                      <span className={`text-sm text-right break-all ${mono ? "font-mono text-teal-600 text-xs" : "text-slate-800 font-medium"}`}>
                        {value ?? "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {isRollingOut && <DeviceRolloutPanel cohort={cohortData} />}

              {/* Only the header carries "Back to Baseline view". Repeating it here
                  gave the page two controls doing the same thing. */}
              <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-1.5 text-sm font-semibold text-[#08148C] border border-teal-300 bg-teal-50 hover:bg-teal-100 px-4 py-2 rounded-lg transition-colors"
                >
                  &#8595; Download Summary
                </button>
                {/* ml-auto rather than relying on justify-between alone: this button
                    is conditional, and without it Download Summary would drift to
                    the centre whenever the baseline is not rolling out. */}
                {isRollingOut && (
                  <button
                    onClick={() => setManageOpen(true)}
                    className="ml-auto flex items-center gap-1.5 text-sm font-semibold text-[#08148C] border border-slate-300 bg-white hover:bg-slate-50 px-4 py-2 rounded-lg transition-colors"
                  >
                    Manage Rollout
                  </button>
                )}
              </div>
              {manageOpen && (
                <ManageRolloutModal
                  firmwareId={id}
                  onClose={() => setManageOpen(false)}
                  // Re-read rather than patching local state from the response:
                  // the outcome counts and the gate are the service's to compute,
                  // and a locally-updated copy would drift from them.
                  onSaved={loadCohort}
                />
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
}

/* ── Manage Rollout ───────────────────────────────────────────────────────
   A rollout already in flight can be widened two ways, and they are the same
   decision: move to a wider tier (raising the cap) and add devices (spending it).
   Both go in ONE request, because the service validates both before writing
   either - sending them separately would let the tier land and the devices then
   be refused, leaving the baseline wider than the cohort anyone agreed to.

   Everything shown here is the service's own answer, fetched from
   GET /rollout-countrywise-device-mapping. Per SRS FMP-NF-003 the browser is
   presentational: a disabled radio and a greyed checkbox are a courtesy, and the
   service refuses the same request independently of what the browser allowed. */

function cohortCap(population, pct) {
  // Mirrors rollout_tiers.tier_cap: floor so the cap is never exceeded, and at
  // least 1 so a country holding a handful of devices can still run a canary.
  if (!population || population <= 0) return 0;
  return Math.max(1, Math.floor((population * pct) / 100));
}

/* The service's own refusal message, which names what is wrong and what to do.
   On an axios error `err.message` is only "Request failed with status code 409" -
   reading that instead of the response body throws away the part the operator
   actually needs, and they are left with a failure that explains nothing. The
   service deliberately puts a usable sentence in `message`, for example
   "This firmware is not approved for US. Approved: in, india."

   Both the fetch and axios shapes are handled: `getRolloutCohort` and the POST use
   axios, while several helpers in lib/api.js throw a plain Error built from the
   response text. */
function serviceMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data?.message) return data.message;
  return err?.message || fallback;
}

function ManageRolloutModal({ firmwareId, onClose, onSaved }) {
  const { toast } = useToast();
  const [cohort, setCohort] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [chosenTier, setChosenTier] = useState(null);
  const [devices, setDevices] = useState([]);
  const [devicesError, setDevicesError] = useState(null);
  const [picked, setPicked] = useState([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getRolloutCohort(firmwareId)
      .then((res) => {
        if (cancelled) return;
        setCohort(res);
        setChosenTier(res.rollout_tier);
      })
      .catch((err) => !cancelled && setError(err.message || "Failed to load the rollout."))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [firmwareId]);

  // Devices live in the countries the rollout already reaches. Loaded once the
  // cohort is known, because that is where the country list comes from.
  const countriesKey = (cohort?.cohort || []).map((r) => r.country).join(",");
  useEffect(() => {
    if (!countriesKey) return;
    let cancelled = false;
    getCountryWiseDeviceList(countriesKey.split(","))
      .then(({ devices: list }) => !cancelled && setDevices(list))
      .catch((err) => !cancelled && setDevicesError(err.message || "Failed to load devices."))
    return () => { cancelled = true; };
  }, [countriesKey]);

  if (loading) {
    return (
      <ModalShell onClose={onClose} title="Manage Rollout">
        <div className="flex items-center justify-center gap-3 py-16">
          <Spinner size="lg" /><span className="text-slate-400 text-sm">Loading rollout...</span>
        </div>
      </ModalShell>
    );
  }
  if (error || !cohort) {
    return (
      <ModalShell onClose={onClose} title="Manage Rollout">
        <p className="py-16 text-center text-sm text-red-500">{error || "No rollout found."}</p>
      </ModalShell>
    );
  }

  const currentTier = cohort.rollout_tier;
  const upgradeable = new Set(cohort.upgrade_tiers || []);
  const tierChanged = chosenTier !== currentTier;
  const pct = ROLLOUT_TIERS[chosenTier]?.pct ?? ROLLOUT_TIERS.full.pct;

  // The cap under the tier the operator is CHOOSING, not the one on record - it is
  // the budget their selection will actually be judged against.
  const cap = cohortCap(cohort.population, pct);
  const headroom = Math.max(0, cap - cohort.targeted);

  // Devices already in the cohort cannot be added again, and are shown as such
  // rather than hidden: "already in this rollout" is information.
  const already = new Set((cohort.cohort || []).flatMap((r) => r.serial_nos));

  // The service answers this; the modal does not decide it. When widening is
  // blocked, BOTH halves are refused - the tier gate and the device gate are the
  // same check - so the whole form is held rather than only the device list.
  const blocked = cohort.widening?.permitted === false;
  const blockedReasons = Object.entries(cohort.widening?.blocked_countries || {});

  const visible = devices.filter((d) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return String(d.serial_no || "").toLowerCase().includes(q);
  });

  const togglePick = (serial) => {
    setPicked((prev) => {
      if (prev.includes(serial)) return prev.filter((s) => s !== serial);
      if (prev.length >= headroom) return prev;   // cap is presentational; service re-checks
      return [...prev, serial];
    });
  };

  const nothingToDo = !tierChanged && picked.length === 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await postRolloutCountrywiseDeviceMapping({
        firmware_id: firmwareId,
        country: (cohort.cohort || []).map((r) => r.country),
        serial_nos: picked,
        ...(tierChanged ? { rollout_tier: chosenTier } : {}),
      });
      const parts = [];
      if (res.tier_changed) parts.push(`tier moved to ${ROLLOUT_TIERS[res.rollout_tier]?.label || res.rollout_tier}`);
      if (picked.length) parts.push(`${picked.length} device${picked.length === 1 ? "" : "s"} added`);
      toast({
        title: "Rollout updated",
        description: parts.join(", ") || undefined,
        variant: "success",
      });
      onSaved?.();
      onClose();
    } catch (err) {
      const message = serviceMessage(err, "Failed to update the rollout.");
      toast({ title: "Could not update the rollout", description: message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-500">Cancel</button>
      <button
        onClick={handleSave}
        disabled={saving || nothingToDo || blocked}
        className="rounded-lg bg-[#08148C] px-5 py-2 text-sm font-semibold text-white hover:bg-[#060f6e] disabled:cursor-not-allowed disabled:opacity-40"
      >
        {saving ? "Saving..." : "Update Rollout"}
      </button>
    </div>
  );

  return (
    <ModalShell onClose={onClose} title="Manage Rollout" footer={footer}>
      {blocked && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
          <p className="text-sm font-semibold text-amber-900">This rollout cannot be widened yet</p>
          {blockedReasons.map(([country, reason]) => (
            <p key={country} className="mt-1 text-xs text-amber-800">
              <span className="font-medium">{country}:</span> {reason.message}
            </p>
          ))}
        </div>
      )}

      {/* ── 1. Tier ── */}
      <section className="mb-5">
        <h4 className="mb-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">Rollout Tier</h4>
        <div className="space-y-2">
          {Object.entries(ROLLOUT_TIERS).map(([key, t]) => {
            const isCurrent = key === currentTier;
            const selectable = isCurrent || (upgradeable.has(key) && !blocked);
            return (
              <label
                key={key}
                className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg border px-3 py-3 sm:px-4 ${
                  selectable ? "cursor-pointer border-slate-200 hover:border-[#08148C]" : "cursor-not-allowed border-slate-100 bg-slate-50 opacity-60"
                } ${chosenTier === key ? "border-[#08148C] bg-blue-50" : ""}`}
              >
                <span className="flex min-w-0 items-center gap-3">
                  <input
                    type="radio"
                    name="rollout-tier"
                    className="shrink-0"
                    checked={chosenTier === key}
                    disabled={!selectable}
                    onChange={() => { setChosenTier(key); setPicked([]); }}
                  />
                  <span className="text-sm font-medium text-slate-800">
                    {t.label} <span className="whitespace-nowrap text-slate-400">(up to {t.pct}%)</span>
                  </span>
                </span>
                <span className="ml-auto shrink-0 text-xs text-slate-500">
                  {isCurrent && "Current"}
                  {!isCurrent && !upgradeable.has(key) && "Cannot narrow a rollout"}
                  {!isCurrent && upgradeable.has(key) && `${cohortCap(cohort.population, t.pct)} devices`}
                </span>
              </label>
            );
          })}
        </div>
      </section>

      {/* ── 2. Devices ── */}
      <section>
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <h4 className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Add Devices</h4>
          <span className="text-xs text-slate-500">
            {cohort.targeted} targeted · cap {cap} · {headroom - picked.length} left
          </span>
        </div>
        {/* The cap is spent. Said plainly, because an entirely greyed-out device
            list with no explanation reads as a broken screen. */}
        {!blocked && headroom === 0 && (
          <p className="mb-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
            The {ROLLOUT_TIERS[chosenTier]?.label} tier is full at {cap} device
            {cap === 1 ? "" : "s"}. Choose a wider tier above to add more.
          </p>
        )}

        {devicesError && <p className="py-2 text-sm text-red-500">{devicesError}</p>}

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search serial number"
          className="mb-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />

        {/* Capped in rem rather than vh: the shell already owns the viewport
            height, so this only stops one long list from crowding out the tier
            pane above it. */}
        <div className="max-h-56 divide-y divide-slate-100 overflow-y-auto overscroll-contain rounded-lg border border-slate-200">
          {visible.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-slate-400">No devices to show.</p>
          )}
          {visible.map((d) => {
            const inCohort = already.has(d.serial_no);
            const isPicked = picked.includes(d.serial_no);
            const disabled = inCohort || blocked || (!isPicked && picked.length >= headroom);
            return (
              <label key={d.serial_no} className={`flex flex-wrap items-center justify-between gap-x-3 px-3 py-2 sm:px-4 ${disabled ? "opacity-50" : "cursor-pointer hover:bg-slate-50"}`}>
                <span className="flex min-w-0 items-center gap-3">
                  <input type="checkbox" className="shrink-0" checked={isPicked} disabled={disabled}
                         onChange={() => togglePick(d.serial_no)} />
                  <span className="truncate font-mono text-xs text-slate-700">{d.serial_no}</span>
                </span>
                {inCohort && <span className="ml-auto shrink-0 text-[11px] text-slate-400">Already in this rollout</span>}
              </label>
            );
          })}
        </div>
      </section>

    </ModalShell>
  );
}

// The dialog is taller than a laptop viewport once the device list has more than a
// handful of rows, so the SHELL owns the height: it is capped at the viewport, the
// header and footer stay put, and only the middle scrolls. Letting the content grow
// instead pushed the title off the top of the screen with no way to reach it.
function ModalShell({ title, onClose, children, footer }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-0 sm:items-center sm:p-4">
      <div className="flex max-h-[100dvh] w-full max-w-xl flex-col rounded-t-xl bg-white shadow-xl sm:max-h-[90vh] sm:rounded-xl">
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
          <h3 className="text-base font-bold text-slate-900 sm:text-lg">{title}</h3>
          <button onClick={onClose} aria-label="Close" className="rounded p-1 hover:bg-slate-100">
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>
        {/* min-h-0 is what actually lets this shrink: without it a flex child keeps
            its content's height and the overflow never engages. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6">{children}</div>
        {footer && (
          <div className="shrink-0 border-t border-slate-100 px-5 py-3 sm:px-6">{footer}</div>
        )}
      </div>
    </div>
  );
}

/* The rollout panel on the Publish Summary: what this baseline is currently
   reaching, per country, with the counts the widening gate reads. */
function DeviceRolloutPanel({ cohort }) {
  if (!cohort) return null;
  const rows = cohort.cohort || [];
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-6 py-3">
        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Device Rollout</span>
        <span className="text-[11px] font-medium text-slate-500">
          {cohort.targeted} of {cohort.cap} (tier cap) · {cohort.population} in scope
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="px-6 py-3 text-sm text-slate-400">No devices targeted yet.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {rows.map((r) => (
            <div key={r.country} className="flex items-start justify-between gap-6 px-6 py-3">
              <span className="w-44 shrink-0 text-sm font-medium text-slate-400">{r.country}</span>
              <span className="text-right text-sm font-medium text-slate-800">
                {r.targeted} targeted
                <span className="block text-xs font-normal text-slate-500">
                  {r.reported} reported · {r.failed} failed · {r.awaiting_report} awaiting
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Draft persistence helpers (mirrors UploadBaselinePage) ──────────── */
const DRAFT_PREFIX = "emed_fw_draft_";

function loadDraftLS(firmwareId) {
  if (!firmwareId) return null;
  try {
    const raw = localStorage.getItem(DRAFT_PREFIX + firmwareId);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveDraftLS(firmwareId, data) {
  if (!firmwareId) return;
  try {
    localStorage.setItem(
      DRAFT_PREFIX + firmwareId,
      JSON.stringify({ ...data, savedAt: new Date().toISOString() })
    );
  } catch {}
}
// ─────────────────────────────────────────────────────────────────────────────

/* ── Rollout tiers ───────────────────────────────────────────────────────
   The tier is chosen at publish and stored on the firmware record. It caps how
   many devices a rollout may target, as a percentage of the devices in the
   countries selected below. These percentages are mirrored server-side in
   services/shared/rollout_tiers.py - change both together. */
const ROLLOUT_TIERS = {
  canary:   { label: "Canary",     pct: 5 },
  extended: { label: "Extended",   pct: 25 },
  full:     { label: "Full Fleet", pct: 100 },
};

/* ── Country list for rollout form ──────────────────────────────────── */
const ROLLOUT_COUNTRIES = [
  { code: "AF", label: "Afghanistan" }, { code: "AL", label: "Albania" }, { code: "DZ", label: "Algeria" },
  { code: "AR", label: "Argentina" }, { code: "AM", label: "Armenia" }, { code: "AU", label: "Australia" },
  { code: "AT", label: "Austria" }, { code: "AZ", label: "Azerbaijan" }, { code: "BH", label: "Bahrain" },
  { code: "BD", label: "Bangladesh" }, { code: "BY", label: "Belarus" }, { code: "BE", label: "Belgium" },
  { code: "BR", label: "Brazil" }, { code: "BG", label: "Bulgaria" }, { code: "CA", label: "Canada" },
  { code: "CL", label: "Chile" }, { code: "CN", label: "China" }, { code: "CO", label: "Colombia" },
  { code: "HR", label: "Croatia" }, { code: "CY", label: "Cyprus" }, { code: "CZ", label: "Czechia" },
  { code: "DK", label: "Denmark" }, { code: "EG", label: "Egypt" }, { code: "EE", label: "Estonia" },
  { code: "FI", label: "Finland" }, { code: "FR", label: "France" }, { code: "GE", label: "Georgia" },
  { code: "DE", label: "Germany" }, { code: "GH", label: "Ghana" }, { code: "GR", label: "Greece" },
  { code: "HU", label: "Hungary" }, { code: "IS", label: "Iceland" }, { code: "IN", label: "India" },
  { code: "ID", label: "Indonesia" }, { code: "IE", label: "Ireland" }, { code: "IL", label: "Israel" },
  { code: "IT", label: "Italy" }, { code: "JP", label: "Japan" }, { code: "JO", label: "Jordan" },
  { code: "KZ", label: "Kazakhstan" }, { code: "KE", label: "Kenya" }, { code: "KW", label: "Kuwait" },
  { code: "LV", label: "Latvia" }, { code: "LB", label: "Lebanon" }, { code: "LT", label: "Lithuania" },
  { code: "LU", label: "Luxembourg" }, { code: "MY", label: "Malaysia" }, { code: "MT", label: "Malta" },
  { code: "MX", label: "Mexico" }, { code: "MD", label: "Moldova" }, { code: "MA", label: "Morocco" },
  { code: "NL", label: "Netherlands" }, { code: "NZ", label: "New Zealand" }, { code: "NG", label: "Nigeria" },
  { code: "NO", label: "Norway" }, { code: "OM", label: "Oman" }, { code: "PK", label: "Pakistan" },
  { code: "PE", label: "Peru" }, { code: "PH", label: "Philippines" }, { code: "PL", label: "Poland" },
  { code: "PT", label: "Portugal" }, { code: "QA", label: "Qatar" }, { code: "RO", label: "Romania" },
  { code: "RU", label: "Russia" }, { code: "SA", label: "Saudi Arabia" }, { code: "RS", label: "Serbia" },
  { code: "SG", label: "Singapore" }, { code: "SK", label: "Slovakia" }, { code: "SI", label: "Slovenia" },
  { code: "ZA", label: "South Africa" }, { code: "KR", label: "South Korea" }, { code: "ES", label: "Spain" },
  { code: "LK", label: "Sri Lanka" }, { code: "SE", label: "Sweden" }, { code: "CH", label: "Switzerland" },
  { code: "TW", label: "Taiwan" }, { code: "TZ", label: "Tanzania" }, { code: "TH", label: "Thailand" },
  { code: "TN", label: "Tunisia" }, { code: "TR", label: "Turkey" }, { code: "UA", label: "Ukraine" },
  { code: "AE", label: "United Arab Emirates" }, { code: "GB", label: "United Kingdom" },
  { code: "US", label: "United States" }, { code: "UY", label: "Uruguay" }, { code: "UZ", label: "Uzbekistan" },
  { code: "VN", label: "Vietnam" }, { code: "ZW", label: "Zimbabwe" },
];
const ROLLOUT_COUNTRY_MAP = Object.fromEntries(ROLLOUT_COUNTRIES.map((c) => [c.code, c.label]));

/* ── Rollout Form Modal ────────────────────────────────────────────────── */
function RolloutFormModal({ formData, onClose, onSubmit, submitting }) {
  const [version, setVersion] = useState(formData.version || "");
  const [selectedCodes, setSelectedCodes] = useState(formData.selectedCodes || []);
  const [countrySearch, setCountrySearch] = useState("");

  const [devices, setDevices] = useState([]);
  const [deviceTotal, setDeviceTotal] = useState(0);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState(null);
  const [selectedSerialNos, setSelectedSerialNos] = useState(formData.selectedSerialNos || []);
  const [deviceSearch, setDeviceSearch] = useState("");

  // The tier chosen at publish caps how many devices this rollout may target, as a
  // percentage of the population in the selected countries. Shown here so the
  // operator can see the budget; the service enforces it independently, because
  // per SRS FMP-NF-003 client-side gating is presentational only.
  const rolloutTier = String(formData.rollout_tier || "full").toLowerCase();
  const tier = ROLLOUT_TIERS[rolloutTier] ?? ROLLOUT_TIERS.full;
  // floor() so the cap is never exceeded (5% of 247 = 12.35 -> 12); at least 1 so a
  // country holding only a handful of devices can still run a canary.
  const deviceCap = deviceTotal > 0
    ? Math.max(1, Math.floor((deviceTotal * tier.pct) / 100))
    : 0;
  const atCap = deviceCap > 0 && selectedSerialNos.length >= deviceCap;

  const filteredCountries = ROLLOUT_COUNTRIES.filter(
    (c) =>
      c.label.toLowerCase().includes(countrySearch.toLowerCase()) ||
      c.code.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const toggle = (code) =>
    setSelectedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );

  const codesKey = selectedCodes.join(",");
  useEffect(() => {
    if (selectedCodes.length === 0) {
      setDevices([]);
      setDeviceTotal(0);
      setSelectedSerialNos([]);
      return;
    }

    let cancelled = false;
    setDevicesLoading(true);
    setDevicesError(null);

    getCountryWiseDeviceList(selectedCodes)
      .then(({ devices: list, total }) => {
        if (cancelled) return;
        setDevices(list);
        setDeviceTotal(total);
        const validSerialNos = new Set(list.map((d) => d.serial_no));
        // Changing the country set moves the denominator, so the cap moves with it.
        // Drop any selection that is no longer valid, then trim to the new cap
        // rather than leaving the operator silently over budget.
        const newCap = total > 0 ? Math.max(1, Math.floor((total * tier.pct) / 100)) : 0;
        setSelectedSerialNos((prev) =>
          prev.filter((s) => validSerialNos.has(s)).slice(0, newCap || undefined)
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setDevicesError(err.message || "Failed to load devices for the selected countries.");
        setDevices([]);
        setDeviceTotal(0);
      })
      .finally(() => {
        if (!cancelled) setDevicesLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codesKey]);

  const filteredDevices = devices.filter(
    (d) =>
      d.serial_no.toLowerCase().includes(deviceSearch.toLowerCase()) ||
      (d.name || "").toLowerCase().includes(deviceSearch.toLowerCase())
  );

  const toggleDevice = (serialNo) =>
    setSelectedSerialNos((prev) => {
      if (prev.includes(serialNo)) return prev.filter((s) => s !== serialNo);
      // Deselecting is always allowed; selecting stops at the tier cap.
      if (deviceCap > 0 && prev.length >= deviceCap) return prev;
      return [...prev, serialNo];
    });

  const readOnlyRows = [
    { label: "Rollout ID",     value: formData.rollout_id },
    { label: "Firmware ID",    value: formData.firmware_id },
    { label: "Target Segment", value: formData.target_segment },
    { label: "Min Device FW",  value: formData.min_device_fw_required },
    { label: "Approved By",    value: formData.approved_by || "—" },
    { label: "Approved At",    value: formData.approved_at ? new Date(formData.approved_at).toLocaleString() : "\u2014" },
    { label: "Uploaded By",    value: formData.uploaded_by },
    { label: "Uploaded At",    value: formData.uploaded_at ? new Date(formData.uploaded_at).toLocaleString() : "\u2014" },
    { label: "Published At",   value: formData.published_at ? new Date(formData.published_at).toLocaleString() : "\u2014" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Decorative backdrop; keyboard users close via the modal's own button. */}
      <div className="absolute inset-0 bg-black/40" aria-hidden="true" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 flex flex-col" style={{ maxHeight: "90vh" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <h2 className="text-base font-bold text-slate-900">Initiate Rollout</h2>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          {/* ── Editable: Version ── */}
          <div>
            <label htmlFor="edit-baseline-version" className="block text-sm font-semibold text-slate-700 mb-1.5">
              Version <span className="text-red-500">*</span>
            </label>
            <input
              id="edit-baseline-version"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="e.g. v1.5.0"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          {/* ── Editable: Countries ── */}
          {/* Composite picker (tags + search + checkbox list) rather than a single
              control, so it is labelled as a group instead of with a <label>. */}
          <fieldset className="min-w-0" aria-labelledby="edit-target-countries-label">
            <p id="edit-target-countries-label" className="block text-sm font-semibold text-slate-700 mb-1.5">
              Target Countries <span className="text-red-500">*</span>
            </p>

            {/* Selected tags */}
            {selectedCodes.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {selectedCodes.map((code) => (
                  <span key={code} className="inline-flex items-center gap-1 text-xs bg-teal-50 border border-teal-200 text-teal-700 rounded-full px-2.5 py-0.5 font-medium">
                    {ROLLOUT_COUNTRY_MAP[code] || code} ({code})
                    <button type="button" onClick={() => toggle(code)} className="text-teal-400 hover:text-red-400 leading-none ml-0.5">&times;</button>
                  </span>
                ))}
              </div>
            )}

            {/* Search */}
            <div className="relative mb-1.5">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                aria-label="Search countries"
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
                placeholder="Search countries..."
                className="w-full pl-9 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {/* Checkbox list */}
            <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-50">
              {filteredCountries.map((c) => {
                const checked = selectedCodes.includes(c.code);
                return (
                  <div // NOSONAR S6819: a native <input type="checkbox"> cannot wrap this row's label content; needs a <label><input class="sr-only"> restructure.
                    key={c.code}
                    role="checkbox"
                    aria-checked={checked}
                    tabIndex={0}
                    onClick={() => toggle(c.code)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggle(c.code);
                      }
                    }}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-sm transition-colors ${
                      checked ? "bg-teal-50" : "hover:bg-slate-50"
                    }`}
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                      checked ? "bg-teal-600 border-teal-600" : "border-slate-300"
                    }`}>
                      {checked && (
                        <svg viewBox="0 0 10 8" className="w-2.5 h-2" fill="none">
                          <path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </span>
                    <span className="font-mono text-xs text-slate-400 w-7 shrink-0">{c.code}</span>
                    <span className={checked ? "font-medium text-[#08148C]" : "text-slate-700"}>{c.label}</span>
                  </div>
                );
              })}
              {filteredCountries.length === 0 && (
                <p className="text-center text-xs text-slate-400 py-4">No countries match &ldquo;{countrySearch}&rdquo;</p>
              )}
            </div>

          </fieldset>

          {/* ── Editable: Devices ── */}
          {/* Composite picker, labelled as a group for the same reason as above. */}
          <fieldset className="min-w-0" aria-labelledby="edit-target-devices-label">
            <p id="edit-target-devices-label" className="block text-sm font-semibold text-slate-700 mb-1.5">
              Target Devices <span className="text-red-500">*</span>
            </p>

            {selectedCodes.length === 0 ? (
              <p className="text-xs text-slate-400 border border-dashed border-slate-200 rounded-lg px-3 py-3 text-center">
                Select at least one country to see its devices.
              </p>
            ) : (
              <>
                {/* Selection budget. Without this the operator has no idea how many
                    devices they have picked, nor how many the tier allows. */}
                {deviceTotal > 0 && (
                  <p className={`text-xs mb-1.5 ${atCap ? "text-amber-600 font-medium" : "text-slate-500"}`}>
                    {selectedSerialNos.length} of {deviceTotal} selected
                    {tier.pct < 100 && <> &middot; {tier.label} cap {deviceCap} ({tier.pct}%)</>}
                    {atCap && tier.pct < 100 && <> &middot; cap reached</>}
                  </p>
                )}

                {/* Selected tags */}
                {selectedSerialNos.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {selectedSerialNos.map((serialNo) => (
                      <span key={serialNo} className="inline-flex items-center gap-1 text-xs bg-teal-50 border border-teal-200 text-teal-700 rounded-full px-2.5 py-0.5 font-medium">
                        {serialNo}
                        <button type="button" onClick={() => toggleDevice(serialNo)} className="text-teal-400 hover:text-red-400 leading-none ml-0.5">&times;</button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Search */}
                <div className="relative mb-1.5">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    value={deviceSearch}
                    onChange={(e) => setDeviceSearch(e.target.value)}
                    placeholder="Search devices..."
                    className="w-full pl-9 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                {/* Checkbox list */}
                <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-50">
                  {devicesLoading && (
                    <p className="text-center text-xs text-slate-400 py-4">Loading devices...</p>
                  )}
                  {!devicesLoading && devicesError && (
                    <p className="text-center text-xs text-red-500 py-4">{devicesError}</p>
                  )}
                  {!devicesLoading && !devicesError && filteredDevices.map((d) => {
                    const checked = selectedSerialNos.includes(d.serial_no);
                    // At the cap, unselected rows are inert - shown disabled rather
                    // than silently ignoring the click.
                    const blocked = !checked && atCap;
                    return (
                      <div // NOSONAR S6819: a native <input type="checkbox"> cannot wrap this row's label content; needs a <label><input class="sr-only"> restructure.
                        key={d.serial_no}
                        role="checkbox"
                        aria-checked={checked}
                        aria-disabled={blocked}
                        title={blocked ? `${tier.label} tier allows at most ${deviceCap} devices.` : undefined}
                        tabIndex={blocked ? -1 : 0}
                        onClick={() => !blocked && toggleDevice(d.serial_no)}
                        onKeyDown={(e) => {
                          if (!blocked && (e.key === "Enter" || e.key === " ")) {
                            e.preventDefault();
                            toggleDevice(d.serial_no);
                          }
                        }}
                        className={`flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                          blocked ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
                        } ${checked ? "bg-teal-50" : !blocked ? "hover:bg-slate-50" : ""}`}
                      >
                        <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                          checked ? "bg-teal-600 border-teal-600" : "border-slate-300"
                        }`}>
                          {checked && (
                            <svg viewBox="0 0 10 8" className="w-2.5 h-2" fill="none">
                              <path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </span>
                        <span className={`font-mono text-xs ${checked ? "font-medium text-[#08148C]" : "text-slate-700"}`}>{d.serial_no}</span>
                        {d.country && <span className="text-xs text-slate-400 ml-auto shrink-0">{d.country}</span>}
                      </div>
                    );
                  })}
                  {!devicesLoading && !devicesError && filteredDevices.length === 0 && (
                    <p className="text-center text-xs text-slate-400 py-4">
                      {devices.length === 0 ? "No devices found for the selected countries." : `No devices match “${deviceSearch}”`}
                    </p>
                  )}
                </div>
              </>
            )}
          </fieldset>

          {/* ── Read-only details ── */}
          <div>
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2">Rollout Details (read\u2011only)</p>
            <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
              {readOnlyRows.map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between px-4 py-2.5 gap-4 bg-slate-50/60">
                  <span className="text-xs font-medium text-slate-400 shrink-0 w-28">{label}</span>
                  <span className="text-xs text-slate-600 text-right break-all">{value || "\u2014"}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between shrink-0">
          <button
            onClick={onClose}
            className="text-sm font-medium text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-4 py-2 bg-white transition-colors"
          >
            Cancel
          </button>
          <button
            disabled={
              !version.trim() ||
              selectedCodes.length === 0 ||
              selectedSerialNos.length === 0 ||
              (deviceCap > 0 && selectedSerialNos.length > deviceCap) ||
              submitting
            }
            onClick={() => onSubmit({ version, selectedCodes, selectedSerialNos })}
            className="flex items-center gap-2 bg-[#08148C] hover:bg-[#060f6e] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors"
          >
            {submitting ? "Initiating..." : "Initiate Rollout \u2192"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Rollout Success Modal ────────────────────────────────────────────── */
function RolloutSuccessModal({ message, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Decorative backdrop; keyboard users close via the modal's own button. */}
      <div className="absolute inset-0 bg-black/40" aria-hidden="true" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 text-center">
        <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-green-100 border border-green-200 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-lg font-bold text-slate-900 mb-1">Rollout Initiated</h3>
        <p className="text-sm text-slate-600 mb-5">{message}</p>
        <button
          onClick={onClose}
          className="w-full text-sm font-semibold text-white bg-[#08148C] hover:bg-[#060f6e] px-5 py-2.5 rounded-lg transition-colors"
        >
          Done
        </button>
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

/* ── Main Page ────────────────────────────────────────────────────────── */
const PAGE_SIZE = 10;

export default function BaselinesPage() {
  const { canAccess, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [baselines, setBaselines] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  // Empty = the service default (newest upload first).
  const [sortBy, setSortBy] = useState({ sort: "", order: "" });
  const [deprecateTarget, setDeprecateTarget] = useState(null);
  const [deprecating, setDeprecating] = useState(false);
  const [deprecateError, setDeprecateError] = useState(null);
  const [holdTarget, setHoldTarget] = useState(null);         // { baseline, action: "pause" | "resume" }
  const [holdSubmitting, setHoldSubmitting] = useState(false);
  const [holdError, setHoldError] = useState(null);
  const [summaryId, setSummaryId] = useState(null);
  const [historyId, setHistoryId] = useState(null);           // DEPRECATED row's History action
  const [resumingId, setResumingId] = useState(null);
  const [rolloutForm, setRolloutForm] = useState(null);      // pre-populated form; null = closed
  const [rolloutSubmitting, setRolloutSubmitting] = useState(false);
  const [rolloutLoadingId, setRolloutLoadingId] = useState(null);
  const [rolloutError, setRolloutError] = useState(null);
  const [rolloutSuccess, setRolloutSuccess] = useState(null);
  const [rolloutInitiatedIds, setRolloutInitiatedIds] = useState([]);

  // Fetch full baseline, build form data, open the form modal (no API call yet)
  const handleRollout = async (b) => {
    setRolloutLoadingId(b.id);
    setRolloutError(null);
    try {
      const full = await getBaseline(b.id);
      const d = full?.data ?? full?.firmware ?? full ?? {};

      const countryArr = d.country ?? d.target_countries ?? b.country ?? b.target_countries ?? [];
      const selectedCodes = Array.isArray(countryArr)
        ? countryArr.map((c) => (typeof c === "object" ? c.code : c)).filter(Boolean)
        : [];

      setRolloutForm({
        rollout_id: `r-${b.id}`,
        firmware_id: b.id,
        version: d.version_string || d.version || b.version || "",
        selectedCodes,
        // Set at publish; caps how many devices this rollout may target. Absent on
        // records published before tiering shipped - those fall back to "full",
        // which preserves their existing unrestricted behaviour.
        rollout_tier: d.rollout_tier || b.rollout_tier || "full",
        min_device_fw_required: d.min_device_fw_required || d.minFw || "",
        // No fallbacks here on purpose. These come from the electronic signature
        // record written at approval; inventing a timestamp or naming whoever
        // happens to be logged in would put fabricated data in the audit trail.
        approved_at: d.approved_at || "",
        approved_by: d.approved_by || "",
        uploaded_at: d.uploaded_at || d.uploadedAt || d.createdAt || b.publishedAt || new Date().toISOString(),
        uploaded_by: d.uploaded_by || d.uploadedBy || user?.email || user?.name || "",
        published_at: d.published_at || d.publishedAt || b.publishedAt || new Date().toISOString(),
        target_segment: d.target_segment || d.segment || b.segment || "",
        baseline: b,
      });
    } catch (err) {
      const msg = serviceMessage(err, "Failed to load rollout details.");
      setRolloutError(msg);
      toast({ title: "Failed to load rollout details", description: msg, variant: "destructive" });
    } finally {
      setRolloutLoadingId(null);
    }
  };

  // Called by the form's submit button — actually fires the API
  const handleRolloutSubmit = async ({ selectedCodes, selectedSerialNos }) => {
    setRolloutSubmitting(true);
    setRolloutError(null);
    try {
      await postRolloutCountrywiseDeviceMapping({
        firmware_id: rolloutForm.firmware_id,
        country: selectedCodes,
        serial_nos: selectedSerialNos,
      });

      const initiatedId = rolloutForm.firmware_id;
      const initiatedVersion = rolloutForm.version || "Selected firmware";

      setRolloutInitiatedIds((prev) => (prev.includes(initiatedId) ? prev : [...prev, initiatedId]));
      setRolloutSuccess(`${initiatedVersion} rollout initiated successfully.`);
      setRolloutForm(null);
    } catch (err) {
      // Shown as a TOAST as well as in the banner. The banner renders below the
      // table, so while the rollout form is open it sits behind the modal and the
      // operator never sees it - the request simply appeared to do nothing. A
      // refusal naming the wrong region is exactly what must reach them.
      const msg = serviceMessage(err, "Failed to initiate rollout.");
      setRolloutError(msg);
      toast({
        title: "Could not initiate the rollout",
        description: msg,
        variant: "destructive",
      });
    } finally {
      setRolloutSubmitting(false);
    }
  };

  // Filtering happens server-side, so the search term is debounced — otherwise
  // every keystroke fires a request at a lambda that scans the whole table.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Only the latest request may update the table. Switching tabs quickly used to
  // let a slower, older response land last and replace the newer one's rows.
  const latestRequest = useRef(0);
  const fetchPage = useCallback((page) => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    setLoadError(null);
    getBaselines({ page, limit: PAGE_SIZE, search: debouncedSearch, state: activeFilter, ...sortBy })
      .then(({ items, total, totalPages }) => {
        if (requestId !== latestRequest.current) return;
        setBaselines(items);
        setPagination({ page, totalPages, total });
      })
      .catch((err) => {
        if (requestId !== latestRequest.current) return;
        console.error("[getBaselines] error:", err);
        // Drop the previous load's rows and totals: left in place they read as the
        // current result for the newly selected tab, and the error below is only
        // rendered for an empty table (DISC-033, TP-FMP-02-TS-01-TC-0006 FT-003).
        setBaselines([]);
        setPagination({ page, totalPages: 1, total: 0 });
        setLoadError(err.message);
      })
      .finally(() => {
        if (requestId === latestRequest.current) setLoading(false);
      });
  }, [activeFilter, debouncedSearch, sortBy]);

  // Refetch whenever the tab, the search term or the sort changes, always from
  // page 1 — the previous page number is meaningless against a different order.
  useEffect(() => { fetchPage(1); }, [fetchPage]);

  // First click sorts ascending; clicking the same column again flips it.
  const toggleSort = (key) => {
    setSortBy((prev) => ({
      sort: key,
      order: prev.sort === key && prev.order === "asc" ? "desc" : "asc",
    }));
  };

  const handleDeprecateConfirm = async (reason) => {
    setDeprecating(true);
    setDeprecateError(null);
    try {
      await deprecateBaseline(deprecateTarget.id, reason);
      setBaselines((prev) =>
        prev.map((b) =>
          b.id === deprecateTarget.id ? { ...b, state: "DEPRECATED", deprecationReason: reason } : b
        )
      );
      setDeprecateTarget(null);
    } catch (err) {
      setDeprecateError(err.message || "Failed to deprecate baseline.");
    } finally {
      setDeprecating(false);
    }
  };

  const handleHoldConfirm = async (reason) => {
    const { baseline, action } = holdTarget;
    setHoldSubmitting(true);
    setHoldError(null);
    try {
      const res = action === "resume"
        ? await resumeFirmwareRollout(baseline.id, reason)
        : await pauseFirmwareRollout(baseline.id, reason);
      const nextState = action === "resume" ? "ROLLOUT" : "PAUSED";
      setBaselines((prev) => prev.map((b) => (b.id === baseline.id ? { ...b, state: nextState } : b)));
      setHoldTarget(null);
      toast({
        title: action === "resume" ? "Rollout resumed" : "Rollout paused",
        description: res?.message || `${baseline.version} ${action === "resume" ? "resumed" : "paused"}.`,
        variant: action === "resume" ? "success" : "warning",
      });
    } catch (err) {
      setHoldError(err.message || `Failed to ${action} rollout.`);
    } finally {
      setHoldSubmitting(false);
    }
  };

  // FMP-DP-004: cancelling (button or backdrop) is audit-logged too (DISC-043).
  // The dialog closes at once; the audit call runs in the background and never
  // blocks the operator.
  const handleDeprecateCancel = () => {
    const target = deprecateTarget;
    setDeprecateTarget(null);
    setDeprecateError(null);
    if (target?.id) {
      cancelDeprecation(target.id).catch((err) =>
        console.error("[Deprecate] cancellation not audited:", err));
    }
  };

  const resumeInWizard = async (b) => {
    setResumingId(b.id);
    try {
      // If a localStorage draft already exists for this firmware, navigate directly —
      // the wizard will restore all form state from it (including partially-filled fields).
      const localDraft = loadDraftLS(b.id);
      if (localDraft?.firmwareId) {
        // If the user went Back from step 2 to step 1, the draft step was reset to 1.
        // But the file is already uploaded, so always resume at step 2.
        if (localDraft.step === 1) {
          saveDraftLS(b.id, { ...localDraft, step: 2 });
        }
        navigate(`/firmware/upload?draftId=${b.id}`);
        return;
      }

      // No local draft: fetch from API and build initial wizard state
      const full = await getBaseline(b.id);
      const d = full?.data ?? full?.firmware ?? full ?? {};

      const countryArr = d.country ?? d.target_countries ?? b.country ?? b.target_countries ?? [];
      const countryCodes = Array.isArray(countryArr)
        ? countryArr.map((c) => (typeof c === "object" ? c.code : c)).filter(Boolean)
        : [];

      const notesRaw = d.release_notes_files ?? d.review_notes_files ?? d.review_notes ?? d.release_notes ?? d.notes_files ?? d.attachments ?? [];
      let noteNames = [];
      if (Array.isArray(notesRaw)) {
        noteNames = notesRaw.map((n) => (typeof n === "object" ? (n.file_name || n.name || n.filename) : n)).filter(Boolean);
      } else if (typeof notesRaw === "string" && notesRaw) {
        noteNames = [notesRaw];
      }

      const wizardData = {
        step: 2,
        firmwareId: d.id || b.id,
        version: d.version_string || d.version || b.version || "",
        fileInfo: { name: d.file_name || b.file_name || "", size: d.file_size || b.file_size || 0 },
        sha256: d.sha256 || b.sha256 || "",
        // `d.type === "Mandatory"` is a boolean and so never nullish, which made
        // every following `??` operand unreachable. Dropped rather than made live:
        // the dead `b.type === "Mandatory"` fallback has never once executed, so
        // enabling it now would be a behaviour change, not a fix (S6638).
        mandatory: d.is_mandatory ?? (d.type === "Mandatory"),
        minFw: d.min_device_fw_required || "",
        segment: d.target_segment || d.segment || d.rollout_segment || "",
        countries: countryCodes,
        reviewNoteFileNames: noteNames,
      };

      // Persist to localStorage so the wizard can restore it on mount
      saveDraftLS(wizardData.firmwareId, wizardData);

      navigate(`/firmware/upload?draftId=${wizardData.firmwareId}`);
    } catch (err) {
      console.error("[resumeInWizard]", err);
    } finally {
      setResumingId(null);
    }
  };

  // The state tab and the search term are applied by baseline-firmware-lambda
  // before it paginates, so `baselines` is already the filtered page and the
  // totals in `pagination` describe the filtered set. Re-filtering here would
  // hide any divergence between the client and server rules.
  const filtered = baselines;

  return (
    <div className="flex flex-col h-full">
      {/* Rollout form modal */}
      {rolloutForm && (
        <RolloutFormModal
          formData={rolloutForm}
          onClose={() => setRolloutForm(null)}
          onSubmit={handleRolloutSubmit}
          submitting={rolloutSubmitting}
        />
      )}

      {rolloutSuccess && (
        <RolloutSuccessModal
          message={rolloutSuccess}
          onClose={() => setRolloutSuccess(null)}
        />
      )}

      {/* Summary overlay */}
      {summaryId && (
        <BaselineSummaryOverlay id={summaryId} onClose={() => setSummaryId(null)} />
      )}
      {historyId && (
        <BaselineSummaryOverlay id={historyId} mode="history" onClose={() => setHistoryId(null)} />
      )}

      {/* Pause / resume modal */}
      {holdTarget && (
        <RolloutHoldModal
          baseline={holdTarget.baseline}
          action={holdTarget.action}
          onConfirm={handleHoldConfirm}
          onCancel={() => { setHoldTarget(null); setHoldError(null); }}
          loading={holdSubmitting}
          error={holdError}
        />
      )}

      {/* Deprecate modal */}
      {deprecateTarget && (
        <DeprecateModal
          baseline={deprecateTarget}
          onConfirm={handleDeprecateConfirm}
          onCancel={handleDeprecateCancel}
          loading={deprecating}
          error={deprecateError}
        />
      )}

      {/* Page header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Firmware Baselines</h1>
          <p className="text-sm text-slate-400 mt-1">
            All firmware versions &middot; Upload &#8594; Sign &#8594; Publish &#8594; Deprecate lifecycle
          </p>
        </div>
        {canAccess("ReleaseManager", "FleetAdmin") && (
          <button
            onClick={() => {
              // Clear any in-session wizard state so the wizard always starts fresh.
              try {
                sessionStorage.removeItem("uploadWizard_step");
                sessionStorage.removeItem("uploadWizard_data");
              } catch {}
              navigate("/firmware/upload");
            }}
            className="flex items-center gap-2 bg-[#08148C] hover:bg-[#060f6e] text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-colors"
          >
            + New Baseline
          </button>
        )}
      </div>

      {/* Search + filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search version, notes..."
            className="pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 w-60"
          />
        </div>
        <div className="flex items-center gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                activeFilter === f
                  ? "bg-[#08148C] text-white border-[#08148C]"
                  : "bg-white text-slate-600 border-slate-300 hover:border-teal-400"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <span className="ml-auto text-sm text-slate-400">{pagination.total} baselines</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100">
              {COLUMNS.map(({ label, sort }) => {
                const active = sort && sortBy.sort === sort;
                const ariaSort = active ? (sortBy.order === "asc" ? "ascending" : "descending") : "none";
                return (
                  <th
                    key={label || "blank"}
                    aria-sort={sort ? ariaSort : undefined}
                    className="px-4 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap"
                  >
                    {sort ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(sort)}
                        className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-slate-700 ${active ? "text-slate-700" : ""}`}
                        title={`Sort by ${label.toLowerCase()}`}
                      >
                        {label}
                        <span aria-hidden="true">{active ? (sortBy.order === "asc" ? "▲" : "▼") : "↕"}</span>
                      </button>
                    ) : label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={COLUMNS.length} className="px-4 py-10 text-center">
                  <div className="flex items-center justify-center gap-2 text-slate-400">
                    <Spinner size="lg" />
                    <span className="text-sm">Loading...</span>
                  </div>
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length} className="px-4 py-10 text-center text-slate-400">
                  {emptyBaselinesMessage(loadError, debouncedSearch, activeFilter)}
                </td>
              </tr>
            )}
            {/* The previous tab's rows are hidden while the new tab loads, so the
                spinner never sits above rows that no longer match the filter. */}
            {!loading && filtered.map((b) => {
              const displayState = b.state === "PENDING" ? "DRAFT" : b.state;
              const st = STATE_STYLE[displayState] || STATE_STYLE.DRAFT;
              const isDeprecated = b.state === "DEPRECATED";
              const isRolloutInitiated = rolloutInitiatedIds.includes(b.id) || displayState === "ROLLOUT";
              // Deliberately excludes ROLLBACK: offering to roll out a firmware the
              // system just pulled for excessive failures would be misleading.
              const isPublishedLike = displayState === "PUBLISHED" || displayState === "ROLLOUT";
              const isRolledBack = displayState === "ROLLBACK";
              const isPaused = displayState === "PAUSED";
              const canHold = canAccess("FleetAdmin", "ReleaseManager");
              return (
                <tr
                  key={b.id}
                  className={`border-b border-slate-50 transition-colors ${isDeprecated ? "opacity-60" : ""}`}
                >
                  <td className="px-4 py-3.5 font-semibold text-slate-800">
                    {b.version ? b.version : <span className="text-slate-400 font-normal">Not defined</span>}
                  </td>

                  <td className="px-4 py-3.5">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${st.pill}`}>
                      {st.dot && <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />}
                      {st.label}
                    </span>
                    {/* Why it was pulled — a bare ROLLBACK pill leaves the operator
                        with no idea how bad the failure rate actually was. */}
                    {isRolledBack && b.rollback_reported_count != null && (
                      <p className="text-[11px] text-red-600 mt-1 whitespace-nowrap">
                        {b.rollback_failed_count}/{b.rollback_reported_count} failed
                        {b.rollback_failure_pct != null && ` (${b.rollback_failure_pct}%)`}
                      </p>
                    )}
                    {isPaused && b.pause_reported_count != null && (
                      <p className="text-[11px] text-amber-700 mt-1 whitespace-nowrap">
                        {b.pause_failed_count}/{b.pause_reported_count} canary failed
                        {b.pause_failure_pct != null && ` (${b.pause_failure_pct}%)`}
                      </p>
                    )}
                  </td>

                  <td className="px-4 py-3.5">
                    {b.mandatory
                      ? <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-600 border border-red-200">Mandatory</span>
                      : <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${isDeprecated ? "border-slate-200 text-slate-400" : "border-teal-300 text-[#08148C]"}`}>Optional</span>
                    }
                  </td>

                  <td className="px-4 py-3.5 text-slate-500 text-xs font-mono whitespace-nowrap" title={b.sha256 || undefined}>
                    {shortHash(b.sha256) ?? <span className="text-slate-300 font-sans">—</span>}
                  </td>
                  <td className="px-4 py-3.5 text-slate-600">{formatSize(b.size)}</td>
                  <td className="px-4 py-3.5 text-slate-500 text-xs whitespace-nowrap">{b.build_date || (b.buildDate ?? "—")}</td>
                  <td className="px-4 py-3.5 text-slate-500 text-xs whitespace-nowrap">{formatBuildTime(b)}</td>
                  <td className="px-4 py-3.5 text-slate-500 text-xs whitespace-nowrap">
                    {b.segment || <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3.5 text-slate-500 text-xs">
                    {(() => {
                      const val = b.country ?? b.target_countries ?? b.countries;
                      if (!Array.isArray(val) || val.length === 0) return <span className="text-slate-300">—</span>;
                      return val.map((c) => (typeof c === "object" ? `${c.name} (${c.code})` : c)).filter(Boolean).join(", ") || <span className="text-slate-300">—</span>;
                    })()}
                  </td>
                  <td className="px-4 py-3.5 text-slate-500 text-xs whitespace-nowrap">{formatPublished(b.publishedAt)}</td>

                  {/* DEVICES column hidden
                  <td className="px-4 py-3.5">
                    {b.state === "PUBLISHED" && b.devicesUpToDate != null ? (
                      <span className={b.devicesUpToDate < 100 ? "font-semibold text-amber-600" : "font-semibold text-slate-700"}>
                        {b.devicesUpToDate}
                      </span>
                    ) : b.state === "PUBLISHED" ? (
                      <span className="text-slate-500">-</span>
                    ) : (
                      <span className="text-slate-400">{b.devicesUpToDate ?? "-"}</span>
                    )}
                    {b.state === "PUBLISHED" && b.totalDevices && (
                      <span className="text-slate-400"> / {b.totalDevices}</span>
                    )}
                  </td>
                  */}

                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2">
                      {displayState === "SIGNED" && (
                        <ActionBtn onClick={() => navigate(`/firmware/${b.id}`)}>Publish</ActionBtn>
                      )}
                      {isPublishedLike && (
                        <ActionBtn
                          disabled={rolloutLoadingId === b.id || isRolloutInitiated}
                          onClick={() => handleRollout(b)}
                        >
                          {rolloutButtonLabel(rolloutLoadingId === b.id, isRolloutInitiated)}
                        </ActionBtn>
                      )}
                      {displayState === "ROLLOUT" && canHold && (
                        <ActionBtn onClick={() => setHoldTarget({ baseline: b, action: "pause" })}>Pause</ActionBtn>
                      )}
                      {isPaused && canHold && (
                        <ActionBtn onClick={() => setHoldTarget({ baseline: b, action: "resume" })}>Resume</ActionBtn>
                      )}
                      {(isPublishedLike || isRolledBack || isPaused) && (
                        <ActionBtn danger onClick={() => setDeprecateTarget(b)}>Deprecate</ActionBtn>
                      )}
                      {displayState === "SIGNED" && (
                        <ActionBtn onClick={() => navigate(`/firmware/${b.id}`)}>View</ActionBtn>
                      )}
                      {/* WP-BL-03: a DEPRECATED row exposes only a History action. */}
                      {displayState === "DEPRECATED" && (
                        <ActionBtn onClick={() => setHistoryId(b.id)}>History</ActionBtn>
                      )}
                      {displayState === "DRAFT" && (
                        <ActionBtn
                          disabled={resumingId === b.id}
                          onClick={() => resumeInWizard(b)}
                        >
                          {resumingId === b.id ? "Loading..." : "Review"}
                        </ActionBtn>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    {/* Not on a DEPRECATED row: History is its only action, and
                        shows the same record. */}
                    {!isDeprecated && <ActionBtn navy onClick={() => setSummaryId(b.id)}>View</ActionBtn>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Rollout API error */}
      {rolloutError && (
        <div className="flex items-center justify-between gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mt-4">
          <p className="text-sm text-red-600 font-medium">{rolloutError}</p>
          <button onClick={() => setRolloutError(null)} className="text-red-400 hover:text-red-600 shrink-0"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex flex-col items-center gap-2 mt-5">
          <div className="flex items-center gap-1">
            {/* Previous */}
            <button
              disabled={pagination.page <= 1}
              onClick={() => fetchPage(pagination.page - 1)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-lg text-slate-600 bg-white hover:bg-slate-50 hover:border-slate-300 disabled:opacity-35 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
              Prev
            </button>

            {/* Page numbers */}
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => {
              const current = pagination.page;
              const total = pagination.totalPages;
              // Show first, last, current ±1, and ellipsis
              const show = p === 1 || p === total || Math.abs(p - current) <= 1;
              const ellipsisBefore = p === current - 2 && current - 2 > 1;
              const ellipsisAfter  = p === current + 2 && current + 2 < total;
              if (ellipsisBefore || ellipsisAfter) {
                return <span key={p} className="px-1 text-slate-400 text-sm select-none">…</span>;
              }
              if (!show) return null;
              return (
                <button
                  key={p}
                  onClick={() => fetchPage(p)}
                  className={`min-w-[36px] px-3 py-1.5 text-sm font-medium border rounded-lg transition-all shadow-sm ${
                    p === current
                      ? "bg-[#08148C] text-white border-[#08148C] shadow-teal-200 shadow"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                  }`}
                >
                  {p}
                </button>
              );
            })}

            {/* Next */}
            <button
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => fetchPage(pagination.page + 1)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-lg text-slate-600 bg-white hover:bg-slate-50 hover:border-slate-300 disabled:opacity-35 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              Next
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ActionBtn({ children, onClick, danger, navy, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`text-xs px-3 py-1.5 rounded-md border font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${actionBtnClass(danger, navy)}`}
    >
      {children}
    </button>
  );
}